import { type AnimationClip, type SkinnedMesh } from 'three'
import { IncompatibleSkeletonError, LoadError, NoAnimationsError } from './AnimationImportErrors.ts'
import { ModalDialog } from '../../ModalDialog.ts'

export default class CustomAnimationValidation {
  /**
   * Validates that animation bones are compatible with target skeleton
   * @throws {IncompatibleSkeletonError} If bones don't match
   * @throws {NoAnimationsError} If no recognizable bone tracks found
   */
  static validate_animation_bones_match (
    animation_clips: AnimationClip[],
    skinned_meshes: SkinnedMesh[]
  ): void {
    if (skinned_meshes.length === 0) {
      throw new Error('No skinned meshes available to validate animations.')
    }

    // Get target bone names from skeleton
    const target_bone_names = new Set<string>()
    skinned_meshes.forEach((skinned_mesh) => {
      skinned_mesh.skeleton.bones.forEach((bone) => {
        target_bone_names.add(bone.name.toLowerCase())
      })
    })

    const animation_bone_names = CustomAnimationValidation.get_animation_bone_names(animation_clips)

    if (animation_bone_names.size === 0) {
      throw new NoAnimationsError('We couldn\'t find any animation tracks in this file.')
    }

    // Check bone name mismatch: every bone referenced by the animation must exist in the
    // target skeleton, otherwise the track has no bone to bind to and Three.js will warn.
    const missing_bones = Array.from(animation_bone_names).filter(bone => !target_bone_names.has(bone))
    if (missing_bones.length > 0) {
      // Truncate the missing-bones list — for an exotic skeleton it can be 60+
      // names long and overflow the modal.
      const truncated = missing_bones.length > 8
        ? missing_bones.slice(0, 8).join(', ') + `, …and ${missing_bones.length - 8} more`
        : missing_bones.join(', ')
      throw new IncompatibleSkeletonError(
        'This animation\'s bone names don\'t match a known Meshinator-compatible rig ' +
        '(Mixamo, Quaternius, Tripo3D, or Meshinator native). ' +
        `Bones we couldn\'t map: ${truncated}. ` +
        'If this is a custom rig, try the Retarget page (Use Your Rigged Model) to map bones manually.'
      )
    }

    // Partial-coverage imports (e.g. Quaternius animations have ~22 bones; Meshinator's
    // human rig has ~64 with fingers) are fine — unmapped target bones simply remain in
    // bind pose. We log this so users get visibility without a hard error.
    if (animation_bone_names.size < target_bone_names.size) {
      console.log(`Animation covers ${animation_bone_names.size}/${target_bone_names.size} bones in the target skeleton. Unmapped bones will hold their bind pose.`)
    }
  }

  /**
   * Gets all bone names referenced in animation clips
   */
  static get_animation_bone_names (animation_clips: AnimationClip[]): Set<string> {
    const bone_names = new Set<string>()
    animation_clips.forEach((clip) => {
      clip.tracks.forEach((track) => {
        const bone_name = CustomAnimationValidation.extract_bone_name_from_track(track.name)
        if (bone_name !== null) {
          bone_names.add(bone_name)
        }
      })
    })
    return bone_names
  }

  /**
   * Extracts bone name from animation track name
   */
  static extract_bone_name_from_track (track_name: string): string | null {
    const bones_match = track_name.match(/\.bones\[([^\]]+)\]\.(?:quaternion|position|scale)$/)
    if (bones_match !== null) {
      return bones_match[1].toLowerCase()
    }

    const simple_match = track_name.match(/^([^.]+)\.(?:quaternion|position|scale)$/)
    if (simple_match !== null) {
      return simple_match[1].toLowerCase()
    }

    return null
  }

  /***
   * Shows different error messages when importing custom animations based on the type of error encountered
   */
  static handle_import_error (error: unknown): { success: boolean, clipCount: number } {
    const error_title = 'Error Importing Animation(s)'
    if (error instanceof NoAnimationsError) {
      new ModalDialog(error_title, 'No animations found in that glb file').show()
      return { success: false, clipCount: 0 }
    }

    // skeleton validation errors
    if (error instanceof IncompatibleSkeletonError) {
      new ModalDialog(error_title, error.message).show()
      return { success: false, clipCount: 0 }
    }

    if (error instanceof LoadError) {
      new ModalDialog(error_title, 'failed to load the animation file').show()
      return { success: false, clipCount: 0 }
    }

    // Unknown error
    new ModalDialog(error_title, 'failed to import animations from the glb file for an unknown reason').show()
    return { success: false, clipCount: 0 }
  }
}
